import {
  MessageBar,
  MessageBarBody,
  Spinner,
  makeStyles,
  tokens,
} from '@fluentui/react-components';
import { Building16Regular, Clock16Regular } from '@fluentui/react-icons';
import { useAtomValue } from 'jotai';
import { useFetch } from '@moko/ui';
import { currentTenantNameAtom, profileDataAtom } from '../../atoms/authAtoms';
import {
  canConfigureSlaRules,
  defaultTimezoneId,
} from '../../services/slaRule';
import { getTenant, parseTenantTimezones } from '../../services/tenantService';
import { toConcreteTenantId } from './pricingSettings';
import SlaRuleEditor from './SlaRuleEditor';

const useStyles = makeStyles({
  page: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalL,
    padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalL} ${tokens.spacingVerticalXL}`,
  },
  context: {
    display: 'flex',
    flexWrap: 'wrap',
    alignItems: 'center',
    columnGap: tokens.spacingHorizontalXL,
    rowGap: tokens.spacingVerticalS,
  },
  contextItem: {
    display: 'inline-flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
  },
  icon: {
    color: tokens.colorBrandForeground1,
  },
  contextValue: {
    color: tokens.colorNeutralForeground1,
    fontWeight: tokens.fontWeightSemibold,
  },
  note: {
    margin: 0,
    maxWidth: '720px',
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase200,
    lineHeight: tokens.lineHeightBase200,
  },
});

const TenantSlaRules = ({
  tenantId,
  tenantName,
}: {
  tenantId: number;
  tenantName: string;
}) => {
  const styles = useStyles();
  const tenantFetch = useFetch({
    queryKey: ['sla-tenant-context', tenantId],
    queryFn: (abortController) => getTenant(abortController, tenantId),
  });
  const timezoneSettled =
    !tenantFetch.loading &&
    tenantFetch.error == null &&
    tenantFetch.data != null;
  const timezoneId = timezoneSettled
    ? defaultTimezoneId(parseTenantTimezones(tenantFetch.data?.timezones))
    : '';

  return (
    <div className={styles.page}>
      <div className={styles.context}>
        <span className={styles.contextItem}>
          <Building16Regular className={styles.icon} />
          Tenant
          <span className={styles.contextValue}>{tenantName || tenantId}</span>
        </span>
        {timezoneId && (
          <span className={styles.contextItem}>
            <Clock16Regular className={styles.icon} />
            Timezone
            <span className={styles.contextValue}>{timezoneId}</span>
          </span>
        )}
        {tenantFetch.loading && (
          <Spinner size="tiny" label="Loading tenant..." />
        )}
      </div>
      <p className={styles.note}>
        These rules control deadline calculations. Saving a rule does not update
        deadlines already recorded on Work.
      </p>
      {timezoneSettled && !timezoneId && (
        <MessageBar intent="warning">
          <MessageBarBody>
            Configure this tenant’s default timezone before calculating SLA
            deadlines.
          </MessageBarBody>
        </MessageBar>
      )}
      <SlaRuleEditor key={tenantId} />
    </div>
  );
};

const SlaRules = () => {
  const styles = useStyles();
  const profile = useAtomValue(profileDataAtom);
  const tenantName = useAtomValue(currentTenantNameAtom);
  const tenantId = toConcreteTenantId(profile.currentTenant);
  const allowed = canConfigureSlaRules(profile.roles, profile.permissions);

  if (tenantId === undefined) {
    return (
      <div className={styles.page}>
        <MessageBar intent="info">
          <MessageBarBody>
            Select a tenant to manage its SLA rules.
          </MessageBarBody>
        </MessageBar>
      </div>
    );
  }

  if (!allowed) {
    return (
      <div className={styles.page}>
        <MessageBar intent="warning">
          <MessageBarBody>
            You don&apos;t have permission to configure SLA rules.
          </MessageBarBody>
        </MessageBar>
      </div>
    );
  }

  return (
    <TenantSlaRules
      key={tenantId}
      tenantId={tenantId}
      tenantName={tenantName}
    />
  );
};

export default SlaRules;
